import { useEffect, useRef } from 'react';

interface Star {
  x: number;
  y: number;
  size: number;
  opacity: number;
  baseOpacity: number;
  twinkleSpeed: number;
  twinklePhase: number;
  driftX: number;
  driftY: number;
}

interface Meteor {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  length: number;
  opacity: number;
  life: number;
  maxLife: number;
}

export default function StarrySky() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;
    let stars: Star[] = [];
    let meteors: Meteor[] = [];
    let nextMeteorTime = 0;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.min(160, Math.floor((window.innerWidth * window.innerHeight) / 12000));
      stars = Array.from({ length: count }, () => ({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        size: Math.random() * 1.2 + 0.3,
        baseOpacity: Math.random() * 0.25 + 0.05,
        opacity: Math.random() * 0.25 + 0.05,
        twinkleSpeed: Math.random() * 0.8 + 0.2,
        twinklePhase: Math.random() * Math.PI * 2,
        driftX: (Math.random() - 0.5) * 0.15,
        driftY: (Math.random() - 0.5) * 0.08,
      }));
    };

    const spawnMeteor = () => {
      const fromRight = Math.random() > 0.5;
      const x = fromRight
        ? window.innerWidth * (0.4 + Math.random() * 0.6)
        : window.innerWidth * (Math.random() * 0.6);
      const y = -20;

      const angleDeg = 30 + Math.random() * 25;
      const angleRad = (angleDeg * Math.PI) / 180;
      const speed = Math.random() * 4 + 5;
      const vx = fromRight ? -Math.cos(angleRad) * speed : Math.cos(angleRad) * speed;
      const vy = Math.sin(angleRad) * speed;

      meteors.push({
        x, y, vx, vy,
        size: Math.random() * 1.2 + 0.6,
        length: Math.random() * 60 + 40,
        opacity: Math.random() * 0.4 + 0.3,
        life: 0,
        maxLife: Math.random() * 40 + 30,
      });
    };

    const draw = (time: number) => {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

      // Static stars
      for (const star of stars) {
        const twinkle = Math.sin(time * 0.001 * star.twinkleSpeed + star.twinklePhase);
        star.opacity = star.baseOpacity + twinkle * 0.06;
        star.opacity = Math.max(0.02, Math.min(0.45, star.opacity));

        star.x += star.driftX;
        star.y += star.driftY;

        if (star.x < -2) star.x = window.innerWidth + 2;
        if (star.x > window.innerWidth + 2) star.x = -2;
        if (star.y < -2) star.y = window.innerHeight + 2;
        if (star.y > window.innerHeight + 2) star.y = -2;

        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
        ctx.fill();
      }

      // Meteors (shooting stars)
      if (time > nextMeteorTime) {
        spawnMeteor();
        nextMeteorTime = time + Math.random() * 18000 + 12000; // 12-30s gap
      }

      meteors = meteors.filter((m) => m.life < m.maxLife);

      for (const m of meteors) {
        m.x += m.vx;
        m.y += m.vy;
        m.life++;

        const progress = m.life / m.maxLife;
        const fadeIn = Math.min(progress * 4, 1);
        const fadeOut = progress > 0.7 ? (1 - progress) / 0.3 : 1;
        const currentOpacity = m.opacity * fadeIn * fadeOut;

        // Draw tail
        const tailX = m.x - m.vx * (m.length / Math.sqrt(m.vx * m.vx + m.vy * m.vy)) * 0.5;
        const tailY = m.y - m.vy * (m.length / Math.sqrt(m.vx * m.vx + m.vy * m.vy)) * 0.5;

        const grad = ctx.createLinearGradient(m.x, m.y, tailX, tailY);
        grad.addColorStop(0, `rgba(255,255,255,${currentOpacity})`);
        grad.addColorStop(0.3, `rgba(255,255,255,${currentOpacity * 0.5})`);
        grad.addColorStop(1, 'rgba(255,255,255,0)');

        ctx.beginPath();
        ctx.moveTo(m.x, m.y);
        ctx.lineTo(tailX, tailY);
        ctx.strokeStyle = grad;
        ctx.lineWidth = m.size;
        ctx.lineCap = 'round';
        ctx.stroke();

        // Head glow
        ctx.beginPath();
        ctx.arc(m.x, m.y, m.size * 1.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${currentOpacity * 0.6})`;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(m.x, m.y, m.size * 0.6, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${currentOpacity})`;
        ctx.fill();
      }

      animationId = requestAnimationFrame(draw);
    };

    resize();
    nextMeteorTime = performance.now() + Math.random() * 3000 + 1500;
    animationId = requestAnimationFrame(draw);

    window.addEventListener('resize', resize);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
}